import React, { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, useLocation } from "react-router";
import { useIntercom } from "react-use-intercom";
import BlankLayout from "go-core/components/Layout/BlankLayout";
import { webviewWithoutSupport } from "go-core/utils/utils";
import { IntercomSecurity } from "../../services/intercom/intercomSecurity";
import { selectOrganization } from "../../services/organizations/selectors";
import { selectIsAuthenticated } from "../../services/session/selectors";
import { selectUser } from "../../services/users/selectors";
import SecurityAcceptTerms from "../AcceptTerms/AcceptTerms";
import LicenseExpiringNotification from "../LicenseExpiringNotification/LicenseExpiringNotification";
import OrganizationNotAcceptedTermsNotification from "../OrganizationAcceptTerms/OrganizationNotAcceptedTermsNotification";
import UnverifiedEmailNotification from "../UnverifiedEmailNotification/UnverifiedEmailNotification";

interface Props {
	goaccounts_url: string;
	navigation: () => JSX.Element;
	baseUrl: string;
	roles?: string[];
}

const AppIndexSecurityComponent: FC<Props> = (props) => {
	const user = useSelector(selectUser);
	const location = useLocation();
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const organization = useSelector(selectOrganization);
	const { boot, update } = useIntercom();
	const [intercomBannerElement, setIntercomBannerElement] = useState<HTMLElement>();
	const bodyObserverRef = useRef<MutationObserver>();
	const [bannersHeights, setBannersHeights] = useState<{
		license: number;
		email: number;
		terms: number;
	}>({
		license: 0,
		email: 0,
		terms: 0,
	});
	const bannersObserverRef = useRef<MutationObserver>();

	const initDomObserver = () => {
		bodyObserverRef.current = new MutationObserver(() => {
			setTimeout(() => {
				updateBannerRef();
			}, 300);
		});
		const body = document.querySelector("body");
		if (body) {
			bodyObserverRef.current.observe(body, {
				attributes: false,
				childList: true,
				subtree: false,
			});
		}
	};

	const cleanupDomObserver = () => {
		bodyObserverRef.current?.disconnect();
	};

	const initBannersObserver = () => {
		bannersObserverRef.current = new MutationObserver(() => {
			measureBannersHeights();
		});

		const bannerIds = [
			"license-expiring-notification",
			"unverified-email-notification",
			"organization-not-accepted-terms-notification",
		];

		bannerIds.forEach((id) => {
			const el = document.getElementById(id);
			if (el) {
				bannersObserverRef.current!.observe(el, {
					childList: true,
					subtree: true,
					characterData: true,
				});
			}
		});
	};

	const cleanupBannersObserver = () => {
		bannersObserverRef.current?.disconnect();
	};

	const updateBannerRef = () => {
		const el = document.getElementsByName("intercom-banner-frame")[0];
		setIntercomBannerElement(el);
	};

	const initIntercom = () => {
		const cfg = IntercomSecurity.cfg(user, organization);
		if (!cfg) return;

		if (webviewWithoutSupport) return;

		if (!IntercomSecurity.isInitialized()) {
			IntercomSecurity.initialize();
			boot(cfg);
		} else {
			update(cfg);
		}
	};

	useEffect(() => {
		initIntercom();
	}, [
		IntercomSecurity.getIntercomId(user),
		IntercomSecurity.userHash(user),
		IntercomSecurity.companyId(organization),
	]);

	useLayoutEffect(() => {
		initDomObserver();
		return () => {
			cleanupDomObserver();
		};
	}, []);

	const measureBannersHeights = useCallback(() => {
		const license = document.getElementById("license-expiring-notification")?.getBoundingClientRect().height ?? 0;
		const email = document.getElementById("unverified-email-notification")?.getBoundingClientRect().height ?? 0;
		const terms =
			document.getElementById("organization-not-accepted-terms-notification")?.getBoundingClientRect().height ??
			0;

		setBannersHeights({
			license,
			email,
			terms,
		});
	}, []);

	useLayoutEffect(() => {
		initBannersObserver();
		measureBannersHeights();

		return () => cleanupBannersObserver();
	}, [organization, user, measureBannersHeights]);

	if (!isAuthenticated) {
		return (
			<Redirect
				to={{
					pathname: "/login",
					state: { from: location },
				}}
			/>
		);
	}

	const getCalculatedIntercomBannerHeight = () => {
		if (intercomBannerElement) {
			if (window.getComputedStyle(intercomBannerElement, null).top === "0px") {
				return intercomBannerElement.clientHeight;
			}

			return 0;
		}

		return 0;
	};

	const getBottomMargin = () => {
		let margin = 0;

		if (intercomBannerElement) {
			if (window.getComputedStyle(intercomBannerElement, null).bottom === "0px") {
				margin += intercomBannerElement.clientHeight;
			}
		}

		return `${margin}px`;
	};

	if (!user.accept_terms) {
		return (
			<div className="page">
				{props.navigation()}
				<SecurityAcceptTerms baseUrl={props.baseUrl} />
			</div>
		);
	}

	const getStyle = () => ({
		marginTop: `${
			bannersHeights.license + bannersHeights.email + bannersHeights.terms + getCalculatedIntercomBannerHeight()
		}px`,
		marginBottom: getBottomMargin(),
	});

	return (
		<BlankLayout>
			<UnverifiedEmailNotification />
			<LicenseExpiringNotification
				organization={organization}
				goaccounts_url={props.goaccounts_url}
				topStyle={`${getCalculatedIntercomBannerHeight() + bannersHeights.email}px`}
			/>
			<OrganizationNotAcceptedTermsNotification
				organization={organization}
				topStyle={`${getCalculatedIntercomBannerHeight() + bannersHeights.license + bannersHeights.email}px`}
				roles={props.roles}
			/>
			<div className="page" style={getStyle()}>
				{props.navigation()}
				{props.children}
			</div>
		</BlankLayout>
	);
};
export default AppIndexSecurityComponent;
